import { useCallback, useEffect, useMemo, useState } from "react";
import { Activity, Flame, Gauge, RefreshCw, RotateCcw, Save, SlidersHorizontal, Zap } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

const inputClass = "w-full h-10 px-3 rounded bg-[hsl(var(--surface-2))] border border-[hsl(var(--border))] text-sm outline-none focus:border-[hsl(var(--primary))]";
const primaryButton = "rounded bg-[hsl(var(--primary))] text-white hover:opacity-90 disabled:opacity-50";
const secondaryButton = "rounded border border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--surface-2))] hover:text-white disabled:opacity-50";

export default function Hype() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState("");
  const [manual, setManual] = useState({ amount: 25, reason: "" });

  const state = data?.state || {};
  const rules = data?.rules || [];
  const thresholds = state.level_thresholds || [0, 25, 75, 150, 300, 500];
  const levelLabels = data?.level_labels || {};
  const nextThreshold = thresholds[Math.min((state.current_level || 0) + 1, 5)] || thresholds[5] || 1;
  const currentThreshold = thresholds[state.current_level || 0] || 0;
  const levelProgress = useMemo(() => {
    const span = Math.max(1, nextThreshold - currentThreshold);
    return Math.max(0, Math.min(100, (((state.current_score || 0) - currentThreshold) / span) * 100));
  }, [currentThreshold, nextThreshold, state.current_score]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: payload } = await api.get("/admin/hype");
      setData(payload);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load hype.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const patchState = (patch) => {
    setData((current) => ({ ...(current || {}), state: { ...((current || {}).state || {}), ...patch } }));
  };

  const patchRule = (eventType, patch) => {
    setData((current) => ({
      ...(current || {}),
      rules: ((current || {}).rules || []).map((rule) => rule.event_type === eventType ? { ...rule, ...patch } : rule),
    }));
  };

  const saveConfig = async () => {
    setSaving("config");
    try {
      const { data: payload } = await api.patch("/admin/hype/config", {
        enabled: state.enabled !== false,
        decay_rate: Number(state.decay_rate || 0),
        level_thresholds: thresholds.map((value) => Number(value || 0)),
        chat_announcements_enabled: state.chat_announcements_enabled !== false,
        chat_announcement_cooldown_seconds: Number(state.chat_announcement_cooldown_seconds || 0),
        level_messages: state.level_messages || {},
        rules: rules.map((rule) => ({
          event_type: rule.event_type,
          enabled: rule.enabled !== false,
          score_amount: Number(rule.score_amount || 0),
          cooldown_seconds: Number(rule.cooldown_seconds || 0),
          scale_per_unit: Number(rule.scale_per_unit || 0),
          max_score_delta: Number(rule.max_score_delta || 0),
        })),
      });
      setData(payload);
      toast.success("Hype rules saved.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save hype.");
    } finally {
      setSaving("");
    }
  };

  const manualAdd = async () => {
    setSaving("manual");
    try {
      const { data: payload } = await api.post("/admin/hype/manual-add", { amount: Number(manual.amount || 0), reason: manual.reason });
      setData(payload);
      setManual({ amount: 25, reason: "" });
      toast.success("Hype added.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add hype.");
    } finally {
      setSaving("");
    }
  };

  const reset = async () => {
    setSaving("reset");
    try {
      const { data: payload } = await api.post("/admin/hype/reset");
      setData(payload);
      toast.success("Hype reset.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not reset hype.");
    } finally {
      setSaving("");
    }
  };

  const testLevel = async (level) => {
    setSaving(`test-${level}`);
    try {
      const { data: payload } = await api.post("/admin/hype/test-level-change", { level });
      setData(payload);
      toast.success(`Tested level ${level}.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not test hype.");
    } finally {
      setSaving("");
    }
  };

  if (loading) {
    return <div className="p-6 text-sm text-[hsl(var(--muted-foreground))]">Loading hype...</div>;
  }

  return (
    <div className="space-y-6" data-testid="hype-page">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <div className="label-eyebrow">Stream momentum</div>
          <h1 className="text-2xl font-semibold tracking-tight mt-1 flex items-center gap-2">
            <Flame className="w-6 h-6 text-[hsl(var(--primary))]" /> Hype System
          </h1>
        </div>
        <button type="button" onClick={load} className={`${secondaryButton} h-10 px-4 inline-flex items-center gap-2`}>
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      <section className="surface p-5">
        <div className="grid grid-cols-1 xl:grid-cols-[1fr_360px] gap-5">
          <div>
            <div className="flex items-center justify-between gap-4">
              <div>
                <div className="label-eyebrow">Current heat</div>
                <div className="mt-2 text-4xl font-semibold">{state.current_score ?? 0}</div>
                <div className="mt-1 text-sm text-[hsl(var(--muted-foreground))]">
                  Level {state.current_level ?? 0}: {state.current_level_label || "quiet"}
                </div>
              </div>
              <Gauge className="w-12 h-12 text-[hsl(var(--primary))]" strokeWidth={1.5} />
            </div>
            <div className="mt-5 h-3 rounded bg-[hsl(var(--surface-2))] overflow-hidden">
              <div className="h-full bg-[hsl(var(--primary))]" style={{ width: `${levelProgress}%` }} />
            </div>
            <div className="mt-2 text-xs text-[hsl(var(--muted-foreground))]">
              Next level at {nextThreshold} points. Decay is {state.decay_rate ?? 0} points per second.
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3">
            <label className="flex items-center justify-between gap-3 p-3 rounded bg-[hsl(var(--surface-2))] border border-[hsl(var(--border))]">
              <span className="text-sm font-medium">Hype tracking enabled</span>
              <input type="checkbox" checked={state.enabled !== false} onChange={(event) => patchState({ enabled: event.target.checked })} />
            </label>
            <NumberField label="Decay rate" value={state.decay_rate ?? 0} step="0.01" onChange={(value) => patchState({ decay_rate: value })} />
            <label className="flex items-center justify-between gap-3 p-3 rounded bg-[hsl(var(--surface-2))] border border-[hsl(var(--border))]">
              <span className="text-sm font-medium">Level chat messages</span>
              <input type="checkbox" checked={state.chat_announcements_enabled !== false} onChange={(event) => patchState({ chat_announcements_enabled: event.target.checked })} />
            </label>
            <NumberField label="Message cooldown seconds" value={state.chat_announcement_cooldown_seconds ?? 300} onChange={(value) => patchState({ chat_announcement_cooldown_seconds: value })} />
            <div className="grid grid-cols-2 gap-2">
              <button type="button" onClick={saveConfig} disabled={saving === "config"} className={`${primaryButton} h-10 inline-flex items-center justify-center gap-2`}>
                <Save className="w-4 h-4" /> Save
              </button>
              <button type="button" onClick={reset} disabled={saving === "reset"} className={`${secondaryButton} h-10 inline-flex items-center justify-center gap-2`}>
                <RotateCcw className="w-4 h-4" /> Reset
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="surface p-5">
        <div className="flex items-center justify-between gap-4 mb-4">
          <div>
            <div className="label-eyebrow">Wild Magic</div>
            <h2 className="text-lg font-semibold mt-1">Random stream effects by hype level</h2>
            <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2 max-w-3xl">
              Wild Magic is the future random-effects pool for hype. As hype climbs, this section will decide which random effects are eligible. Hype random VIP is temporarily disabled; VIP steals now use the Steal a VIP channel point redeem on /app/vips.
            </p>
          </div>
          <Zap className="w-5 h-5 text-[hsl(var(--primary))]" />
        </div>
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-3">
          <WildMagicCard level="1" title="Warming up" body="Small cosmetic or chat-only effects. Good for low-risk fun." />
          <WildMagicCard level="2" title="Active" body="Light overlay moments, tiny bonuses, or soft viewer prompts." />
          <WildMagicCard level="3" title="Popping off" body="Hype VIP disabled. Use Steal a VIP channel points instead." />
          <WildMagicCard level="4" title="Chaos" body="Future medium-impact effects like alert chains, Wild Magic pools, or temporary interaction changes." />
          <WildMagicCard level="5" title="Maximum chaos" body="Future high-impact effects. Should stay rate-limited and streamer-approved." />
          <div className="p-4 rounded bg-[hsl(var(--surface-2))] border border-[hsl(var(--border))]">
            <div className="text-sm font-semibold">Connected today</div>
            <div className="mt-2 text-xs text-[hsl(var(--muted-foreground))] leading-relaxed">
              Hype random VIP: temporarily disabled · Steal VIP redeem: /app/vips
            </div>
          </div>
        </div>
      </section>

      <section className="surface p-5">
        <div className="flex items-center justify-between gap-4 mb-4">
          <div>
            <div className="label-eyebrow">Chat messages</div>
            <h2 className="text-lg font-semibold mt-1">Level-up announcements</h2>
            <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">
              Messages fire only when hype rises into a level, with a per-level cooldown to prevent loops.
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {[1, 2, 3, 4, 5].map((level) => (
            <TextField
              key={level}
              label={`Level ${level} message`}
              value={(state.level_messages || {})[String(level)] || ""}
              onChange={(message) => patchState({ level_messages: { ...(state.level_messages || {}), [String(level)]: message } })}
            />
          ))}
        </div>
      </section>

      <section className="surface p-5">
        <div className="flex items-center justify-between gap-4 mb-4">
          <div>
            <div className="label-eyebrow">Level thresholds</div>
            <h2 className="text-lg font-semibold mt-1">Hype levels</h2>
          </div>
          <SlidersHorizontal className="w-5 h-5 text-[hsl(var(--primary))]" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-6 gap-3">
          {thresholds.map((value, index) => (
            <div key={index}>
              <NumberField
                label={`Level ${index}`}
                value={value}
                onChange={(next) => {
                  const updated = [...thresholds];
                  updated[index] = next;
                  patchState({ level_thresholds: updated });
                }}
              />
              <div className="mt-1 text-xs text-[hsl(var(--muted-foreground))]">{levelLabels[index] || ""}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="surface p-5">
        <div className="flex items-center justify-between gap-4 mb-4">
          <div>
            <div className="label-eyebrow">Sources</div>
            <h2 className="text-lg font-semibold mt-1">Event weights</h2>
          </div>
          <Activity className="w-5 h-5 text-[hsl(var(--primary))]" />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase text-[hsl(var(--muted-foreground))]">
                <th className="py-2 pr-3">Source</th>
                <th className="py-2 px-3">On</th>
                <th className="py-2 px-3">Score</th>
                <th className="py-2 px-3">Cooldown</th>
                <th className="py-2 px-3">Scale per</th>
                <th className="py-2 pl-3">Cap</th>
              </tr>
            </thead>
            <tbody>
              {rules.map((rule) => (
                <tr key={rule.event_type} className="border-t border-[hsl(var(--border))]">
                  <td className="py-2 pr-3 min-w-48">
                    <div className="font-medium">{rule.label}</div>
                    <div className="text-xs text-[hsl(var(--muted-foreground))]">{rule.event_type}</div>
                  </td>
                  <td className="py-2 px-3"><input type="checkbox" checked={rule.enabled !== false} onChange={(event) => patchRule(rule.event_type, { enabled: event.target.checked })} /></td>
                  <td className="py-2 px-3 min-w-28"><NumberField value={rule.score_amount ?? 0} onChange={(value) => patchRule(rule.event_type, { score_amount: value })} /></td>
                  <td className="py-2 px-3 min-w-28"><NumberField value={rule.cooldown_seconds ?? 0} onChange={(value) => patchRule(rule.event_type, { cooldown_seconds: value })} /></td>
                  <td className="py-2 px-3 min-w-28"><NumberField value={rule.scale_per_unit ?? 0} onChange={(value) => patchRule(rule.event_type, { scale_per_unit: value })} /></td>
                  <td className="py-2 pl-3 min-w-28"><NumberField value={rule.max_score_delta ?? 0} onChange={(value) => patchRule(rule.event_type, { max_score_delta: value })} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <section className="surface p-5">
          <div className="label-eyebrow mb-3">Manual controls</div>
          <div className="grid grid-cols-1 md:grid-cols-[140px_1fr_auto] gap-3 items-end">
            <NumberField label="Amount" value={manual.amount} onChange={(amount) => setManual({ ...manual, amount })} />
            <TextField label="Reason" value={manual.reason} onChange={(reason) => setManual({ ...manual, reason })} />
            <button type="button" onClick={manualAdd} disabled={saving === "manual"} className={`${primaryButton} h-10 px-4 inline-flex items-center gap-2`}>
              <Zap className="w-4 h-4" /> Add
            </button>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            {[1, 2, 3, 4, 5].map((level) => (
              <button key={level} type="button" onClick={() => testLevel(level)} className={`${secondaryButton} h-9 px-3 text-xs`}>
                Test Level {level}
              </button>
            ))}
          </div>
        </section>

        <section className="surface p-5">
          <div className="label-eyebrow mb-3">Recent hype events</div>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {(data?.events || []).length === 0 && <div className="text-sm text-[hsl(var(--muted-foreground))]">No hype events yet.</div>}
            {(data?.events || []).map((event) => (
              <div key={event.id} className="p-3 rounded bg-[hsl(var(--surface-2))] border border-[hsl(var(--border))]">
                <div className="flex items-center justify-between gap-3">
                  <div className="font-medium">{event.event_type}</div>
                  <div className="font-mono text-sm">{event.score_delta > 0 ? "+" : ""}{event.score_delta}</div>
                </div>
                <div className="mt-1 text-xs text-[hsl(var(--muted-foreground))]">
                  {event.source} · Level {event.level_before} to {event.level_after} · {event.created_at}
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}

function WildMagicCard({ level, title, body, active = false }) {
  return (
    <div className={`p-4 rounded border ${active ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/10" : "border-[hsl(var(--border))] bg-[hsl(var(--surface-2))]"}`}>
      <div className="text-xs uppercase text-[hsl(var(--muted-foreground))]">Level {level}</div>
      <div className="mt-1 font-semibold">{title}</div>
      <div className="mt-2 text-xs text-[hsl(var(--muted-foreground))] leading-relaxed">{body}</div>
    </div>
  );
}

function NumberField({ label = "", value, onChange, step = "1" } = {}) {
  return (
    <label className="block">
      {label && <span className="block text-xs uppercase text-[hsl(var(--muted-foreground))] mb-1">{label}</span>}
      <input className={inputClass} type="number" step={step} value={value} onChange={(event) => onChange(Number(event.target.value || 0))} />
    </label>
  );
}

function TextField({ label = "", value, onChange } = {}) {
  return (
    <label className="block">
      <span className="block text-xs uppercase text-[hsl(var(--muted-foreground))] mb-1">{label}</span>
      <input className={inputClass} value={value} onChange={(event) => onChange(event.target.value)} />
    </label>
  );
}
